/**
 * The Egypt fiscal contract (§5, ADR-0005), pinned in one file.
 *
 * Read directly off `/tmp/integrate` (`integrate/wave-a`, backend green) —
 * `src/modules/fiscal/{fiscal.controller.ts,fiscal-documents.controller.ts,
 * fiscal-profiles.service.ts,fiscal-documents.service.ts,dto/*}` and
 * `docs/fiscal.md` Part 1, which is written for this exact operator. Every
 * shape below is copied from there, not guessed, so a late backend change is a
 * diff to this one file rather than a hunt through every screen that reads it.
 *
 * THE ONE SENTENCE (`docs/fiscal.md`): never make external authority
 * availability the only source of local transaction truth. A rejected or
 * pending fiscal document is an operator TASK, never a blocked sale — which is
 * why this screen exists at all, and why every status in
 * `FiscalDocumentStatus` below is a real, visible state rather than a spinner.
 *
 * The fiscal document is its OWN aggregate (ADR-0005 §1): it is never rendered
 * as a property of a `Check` or a `Payment`, and nothing here fetches or shows
 * a check's own fields beyond the few identifying columns the reconciliation
 * read already embeds.
 */

/** `FiscalRegime` — `prisma/schema.prisma`. One member today; the registry is built for more. */
export const FiscalRegime = {
  EG_ETA: 'EG_ETA',
} as const;
export type FiscalRegime = (typeof FiscalRegime)[keyof typeof FiscalRegime];

/** Stamped on the profile AND copied onto every document at build time (ADR-0005 §7). */
export const FiscalEnvironment = {
  SANDBOX: 'SANDBOX',
  PRODUCTION: 'PRODUCTION',
} as const;
export type FiscalEnvironment = (typeof FiscalEnvironment)[keyof typeof FiscalEnvironment];

export const FiscalProfileStatus = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  DISABLED: 'DISABLED',
} as const;
export type FiscalProfileStatus = (typeof FiscalProfileStatus)[keyof typeof FiscalProfileStatus];

/**
 * `DRAFT → QUEUED → SUBMITTING → AWAITING_AUTHORITY → ACCEPTED`, with
 * `REJECTED`/`CANCELLED`/`ABANDONED` off the side (ADR-0005 §1). This is the
 * vocabulary the reconciliation screen is built around — `pending` is the
 * first four, `rejected` is the operator's queue, and `retrying` is QUEUED
 * with attempts behind it, which `fiscal-documents.service.ts#reconciliation`
 * computes as a fact distinct from the status itself.
 */
export const FiscalDocumentStatus = {
  DRAFT: 'DRAFT',
  QUEUED: 'QUEUED',
  SUBMITTING: 'SUBMITTING',
  AWAITING_AUTHORITY: 'AWAITING_AUTHORITY',
  ACCEPTED: 'ACCEPTED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
  ABANDONED: 'ABANDONED',
} as const;
export type FiscalDocumentStatus =
  (typeof FiscalDocumentStatus)[keyof typeof FiscalDocumentStatus];

export const FiscalDocumentKind = {
  SALE: 'SALE',
  REFUND: 'REFUND',
  CANCELLATION: 'CANCELLATION',
} as const;
export type FiscalDocumentKind = (typeof FiscalDocumentKind)[keyof typeof FiscalDocumentKind];

// ---------------------------------------------------------------------------
// routes
// ---------------------------------------------------------------------------

export const FISCAL_ROUTES = {
  regimes: '/fiscal/regimes',

  profiles: '/fiscal/profiles',
  profile: (id: string) => `/fiscal/profiles/${id}`,
  activate: (id: string) => `/fiscal/profiles/${id}/activate`,
  disable: (id: string) => `/fiscal/profiles/${id}/disable`,
  environment: (id: string) => `/fiscal/profiles/${id}/environment`,
  verify: (id: string) => `/fiscal/profiles/${id}/verify`,

  credentials: (id: string) => `/fiscal/profiles/${id}/credentials`,
  credential: (id: string, kind: string) => `/fiscal/profiles/${id}/credentials/${kind}`,

  taxMappings: '/fiscal/mappings/taxes',
  taxMapping: (id: string) => `/fiscal/mappings/taxes/${id}`,
  itemMappings: '/fiscal/mappings/items',
  itemMapping: (id: string) => `/fiscal/mappings/items/${id}`,
  validation: '/fiscal/configuration/validation',

  reconciliation: '/fiscal/reconciliation',
  documents: '/fiscal/documents',
  document: (id: string) => `/fiscal/documents/${id}`,
  buildForCheck: (checkId: string) => `/fiscal/checks/${checkId}/document`,
  retry: (id: string) => `/fiscal/documents/${id}/retry`,
  submit: (id: string) => `/fiscal/documents/${id}/submit`,
  cancel: (id: string) => `/fiscal/documents/${id}/cancel`,
  adjust: (id: string) => `/fiscal/documents/${id}/adjust`,
  abandon: (id: string) => `/fiscal/documents/${id}/abandon`,
} as const;

/**
 * The permission keys the API enforces (`permission-catalogue.json`), so a
 * screen never offers a control guaranteed to answer 403.
 *
 * `view` reads. `configure` edits the identity and mappings every future
 * document is reported under. `credentials` replaces the secret that lets the
 * venue speak to the authority AS itself — deliberately split from
 * `configure`, because the person who maintains a menu's EGS codes is not
 * automatically the person who should hold the third. `retry`/`cancel`/
 * `resolve` are the three operator-resolution keys `fiscal-documents.controller.ts`
 * enforces one at a time.
 */
export const FISCAL_PERMISSIONS = {
  view: 'fiscal.view',
  configure: 'fiscal.configure',
  credentials: 'fiscal.credentials',
  retry: 'fiscal.retry',
  cancel: 'fiscal.cancel',
  resolve: 'fiscal.resolve',
} as const;

// ---------------------------------------------------------------------------
// shapes
// ---------------------------------------------------------------------------

export interface FiscalAddress {
  country?: string | null;
  governorate?: string | null;
  city?: string | null;
  street?: string | null;
  buildingNumber?: string | null;
  postalCode?: string | null;
}

/** What `GET /fiscal/regimes` names for each supported regime — the configuration screen's script. */
export interface FiscalRegimeDescription {
  regime: FiscalRegime;
  displayName: string;
  taxpayerIdLabel: string;
  settingsKeys: { key: string; label: string; required: boolean }[];
  itemCodeSystems: string[];
  supports: {
    cancellation: boolean;
    cancellationWindowHours: number | null;
    adjustment: boolean;
  };
  sandboxAvailable: boolean;
}

/** `FiscalIssue` — one thing wrong with a profile, a mapping, or the venue's configuration. */
export interface FiscalIssue {
  code: string;
  message: string;
  field?: string | null;
}

export interface FiscalProfile {
  id: string;
  tenantId: string;
  branchId: string | null;
  regime: FiscalRegime;
  environment: FiscalEnvironment;
  status: FiscalProfileStatus;
  taxpayerId: string;
  legalName: string;
  tradeName: string | null;
  activityCode: string | null;
  branchCode: string | null;
  address: FiscalAddress | null;
  regimeSettings: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  /** `GET /fiscal/profiles/:id` only — the adapter's own `validateProfile()` run fresh. */
  issues?: FiscalIssue[];
}

/** `GET /fiscal/profiles/:id/credentials` — never the secret, only what is set and a hint. */
export interface FiscalCredentialSummary {
  kind: string;
  hint: string;
  updatedAt: string;
}

export interface FiscalTaxMapping {
  id: string;
  tenantId: string;
  regime: FiscalRegime;
  code: string;
  label: string;
  taxType: string;
  taxSubType: string | null;
  /** A FRACTION — `0.1400`, never `14`. */
  rate: string;
  isDefault: boolean;
  regimeSettings: Record<string, unknown> | null;
}

export interface FiscalItemMapping {
  id: string;
  tenantId: string;
  regime: FiscalRegime;
  catalogItemId: string;
  codeSystem: string;
  itemCode: string;
  unitCode: string | null;
  taxMappingId: string | null;
  regimeSettings: Record<string, unknown> | null;
  catalogItem?: { id: string; name: string; sku?: string | null } | null;
}

/** `GET /fiscal/configuration/validation` — everything wrong, in one answer. `ready: true` means nothing left. */
export interface FiscalConfigurationValidation {
  ready: boolean;
  profileIssues: FiscalIssue[];
  unmappedItems: { id: string; name: string; sku?: string | null }[];
  unmappedItemCount: number;
  invalidMappings: { itemMappingId: string; catalogItemId: string; issues: FiscalIssue[] }[];
  hasDefaultTaxMapping: boolean;
}

/** One append-only attempt at the authority — `fiscal_submissions`. */
export interface FiscalSubmission {
  id: string;
  documentId: string;
  attempt: number;
  outcome: string;
  requestBody: unknown;
  responseBody: unknown;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
}

/** One status transition — `fiscal_document_events`, append-only. */
export interface FiscalDocumentEvent {
  id: string;
  documentId: string;
  fromStatus: FiscalDocumentStatus | null;
  toStatus: FiscalDocumentStatus;
  reason: string;
  actorId: string | null;
  detail: unknown;
  createdAt: string;
}

/** A row of `GET /fiscal/documents` — `DOCUMENT_LIST_SELECT`, deliberately without `payload`. */
export interface FiscalDocumentRow {
  id: string;
  number: string;
  status: FiscalDocumentStatus;
  kind: FiscalDocumentKind;
  regime: FiscalRegime;
  environment: FiscalEnvironment;
  branchId: string;
  checkId: string;
  uuid: string;
  externalId: string | null;
  totalAmount: string;
  taxAmount: string;
  currency: string;
  attempts: number;
  nextAttemptAt: string | null;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
  issuedAt: string;
  submittedAt: string | null;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
}

/** `GET /fiscal/documents/:id` — the row plus its complete, append-only history. */
export interface FiscalDocumentDetail extends FiscalDocumentRow {
  submissions: FiscalSubmission[];
  events: FiscalDocumentEvent[];
  profile: { id: string; regime: FiscalRegime; environment: FiscalEnvironment; taxpayerId: string } | null;
  check: { id: string; number: string; status: string; grandTotal: string } | null;
}

/** `GET /fiscal/reconciliation` — the reconciliation screen's whole data, in one call. */
export interface FiscalReconciliation {
  counts: Record<FiscalDocumentStatus, number>;
  amounts: Record<FiscalDocumentStatus, string>;
  /** DRAFT + QUEUED + SUBMITTING + AWAITING_AUTHORITY — sales not yet reported. */
  pending: number;
  accepted: number;
  /** The operator's queue. */
  rejected: number;
  /** QUEUED *with attempts behind it* — not the same as a young queue. */
  retrying: number;
  /** The single most useful number on the screen: how long the oldest unreported sale has waited. */
  oldestPending: {
    id: string;
    number: string;
    issuedAt: string;
    status: FiscalDocumentStatus;
    attempts: number;
  } | null;
  /** The three problems behind four hundred rejections, grouped on the stable code. */
  rejectionsByCode: { code: string; count: number }[];
}

// ---------------------------------------------------------------------------
// request bodies
// ---------------------------------------------------------------------------

export interface CreateFiscalProfileBody {
  regime: FiscalRegime;
  branchId?: string;
  environment?: FiscalEnvironment;
  taxpayerId: string;
  legalName: string;
  tradeName?: string;
  activityCode?: string;
  branchCode?: string;
  address?: FiscalAddress;
  regimeSettings?: Record<string, unknown>;
}

export type UpdateFiscalProfileBody = Partial<Omit<CreateFiscalProfileBody, 'regime' | 'environment'>>;

export interface UpsertFiscalTaxMappingBody {
  regime: FiscalRegime;
  code: string;
  label: string;
  taxType: string;
  taxSubType?: string;
  rate: string;
  isDefault?: boolean;
  regimeSettings?: Record<string, unknown>;
}

export interface UpsertFiscalItemMappingBody {
  regime: FiscalRegime;
  catalogItemId: string;
  codeSystem: string;
  itemCode: string;
  unitCode?: string;
  taxMappingId?: string;
  regimeSettings?: Record<string, unknown>;
}

/** Every operator resolution (`retry`/`cancel`/`adjust`/`abandon`) requires a reason on the three that end a document's story. */
export interface ResolveFiscalDocumentBody {
  reason: string;
  operationId?: string;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/** Surface the API's own message rather than a generic failure line. */
export function apiMessage(error: unknown, fallback: string): string {
  const payload = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(payload)) return payload.filter(Boolean).join(' · ');
  if (typeof payload === 'string' && payload.trim()) return payload;
  return fallback;
}

/** The HTTP status of a failed request, when there was a response at all — distinguishes "no rows" from "the query failed". */
export function errorStatus(error: unknown): number | null {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === 'number' ? status : null;
}

/** `PENDING_STATUSES` — mirrors `reconciliation()`'s own definition of "not yet reported". */
export const PENDING_STATUSES: readonly FiscalDocumentStatus[] = [
  FiscalDocumentStatus.DRAFT,
  FiscalDocumentStatus.QUEUED,
  FiscalDocumentStatus.SUBMITTING,
  FiscalDocumentStatus.AWAITING_AUTHORITY,
];

/** A document a reason-bearing operator action makes sense for right now. */
export function isActionable(status: FiscalDocumentStatus): boolean {
  return status === FiscalDocumentStatus.REJECTED || status === FiscalDocumentStatus.QUEUED;
}

/**
 * A fresh replay key for a mutating call. `retry`/`cancel`/`adjust`/`abandon`/
 * `build` all accept `operationId` and run through the backend's `runOnce`, so
 * a back-office screen retried on a flaky connection cannot abandon a document
 * twice — matching `Pages/Billing/lib.ts#idempotencyKey`.
 */
export function newOperationId(prefix: string): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `dashboard:${prefix}:${random}`;
}
